const REACT_APP_BFF_URL = process.env.REACT_APP_BFF_URL;

const mapCartForAgent = () => {
  if (typeof window === 'undefined') {
    return []
  }

  try {
    const rawCart = JSON.parse(localStorage.getItem('cart') || '{}')
    if (!rawCart || typeof rawCart !== 'object') {
      return []
    }

    return Object.entries(rawCart)
      .map(([productId, item]) => {
        if (!productId || !item || typeof item !== 'object') {
          return null
        }

        const measuresPayload = Array.isArray(item.measures)
          ? item.measures
              .map((measure) => {
                if (!measure || typeof measure !== 'object') {
                  return null
                }

                const quantityNumber = Number(measure.quantity)
                const quantity = Number.isFinite(quantityNumber) ? quantityNumber : null

                const prices = Array.isArray(measure.prices) ? measure.prices : []
                const primaryPrice = prices.find((price) =>
                  price && typeof price === 'object' && Number.isFinite(Number(price.precioTotal)),
                )

                const payload = {
                  measure:
                    typeof measure.measure === 'string' && measure.measure.trim()
                      ? measure.measure.trim()
                      : 'General',
                }

                if (Number.isFinite(quantity) && quantity > 0) {
                  payload.quantity = quantity
                }

                if (primaryPrice) {
                  if (
                    typeof primaryPrice.label === 'string' &&
                    primaryPrice.label.trim() &&
                    primaryPrice.label.trim().toLowerCase() !== payload.measure.toLowerCase()
                  ) {
                    payload.unit_label = primaryPrice.label.trim()
                  }

                  const unitPriceNumber = Number(primaryPrice.precioTotal)
                  if (Number.isFinite(unitPriceNumber) && unitPriceNumber > 0) {
                    payload.unit_price = unitPriceNumber
                  }
                }

                return payload
              })
              .filter((entry) => entry && (entry.quantity === undefined || entry.quantity > 0))
          : []

        if (measuresPayload.length === 0) {
          return null
        }

        const totalQuantity = measuresPayload.reduce((sum, entry) => {
          const value = Number(entry.quantity)
          return Number.isFinite(value) ? sum + value : sum
        }, 0)

        const cartItem = {
          id: productId,
        }

        if (typeof item.name === 'string' && item.name.trim()) {
          cartItem.name = item.name.trim()
        }

        if (Array.isArray(item.images) && item.images.length > 0) {
          const primaryImage = item.images.find((image) => typeof image === 'string' && image.trim())
          if (primaryImage) {
            cartItem.image = primaryImage.trim()
          }
        }

        cartItem.measures = measuresPayload
        if (totalQuantity > 0) {
          cartItem.total_quantity = totalQuantity
        }

        return cartItem
      })
      .filter(Boolean)
  } catch (error) {
    console.warn('No se pudo leer el carrito para el agente', error)
    return []
  }
}

/**
 * Realiza una búsqueda inteligente vía backend AI Agent
 * @param {string} query - Texto del usuario
 * @param {Object} opts - Opciones adicionales
 * @param {('semantic'|'keyword'|'hybrid')} [opts.search_type='semantic']
 * @param {number} [opts.limit=8]
 * @param {boolean} [opts.include_explanation=true]
 * @param {Object} [opts.filters]
 * @returns {Promise<Object>} Respuesta del backend (AISearchResponse)
 */
export const searchAI = async (
  query,
  { search_type = 'semantic', limit = 8, include_explanation = true, filters = undefined } = {}
) => {
  const response = await fetch(`${REACT_APP_BFF_URL}/ai/search`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ query, search_type, limit, include_explanation, filters }),
  });

  const data = await response.json();
  if (!response.ok) {
    const detail = data?.detail || 'Error en búsqueda AI';
    throw new Error(detail);
  }
  return data;
};

/**
 * Obtiene sugerencias rápidas de búsqueda
 * @param {string} query
 */
export const getSuggestions = async (query, limit = 5) => {
  const url = new URL(`${REACT_APP_BFF_URL}/ai/suggestions`);
  url.searchParams.set('query', query);
  url.searchParams.set('limit', String(limit));
  const response = await fetch(url);
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.detail || 'Error obteniendo sugerencias');
  }
  return data;
};

/**
 * Conversación con el agente básico (LangChain)
 * @param {Array<{role: 'user'|'assistant'|'system', content: string}>} messages
 */
export const chatWithAgent = async (messages) => {
  const cart = mapCartForAgent();
  const payload = {
    messages: messages.map(({ role, content }) => ({ role, content })),
    ...(cart.length > 0 ? { cart } : {}),
  };

  const response = await fetch(`${REACT_APP_BFF_URL}/agent/chat`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.detail || 'Error en el agente');
  }
  return data; // { reply: { role, content }, model, finish_reason }
};

const normalizeOrderForAgent = (order) => {
  if (!order || typeof order !== 'object') return null;

  const pickFirst = (...candidates) => {
    for (const candidate of candidates) {
      if (candidate !== undefined && candidate !== null && `${candidate}`.trim() !== '') {
        return candidate;
      }
    }
    return undefined;
  };

  const total =
    order.total ||
    (order.total_amount
      ? {
          amount: order.total_amount,
          currency: order.currency || order.total_currency,
        }
      : undefined);

  const snapshot = {
    id: pickFirst(order.quote_number, order.id, order.order_id, order.orderId, order.code, order.uid),
    status: pickFirst(order.status, order.validity),
    date: pickFirst(order.quote_date, order.date, order.created_at, order.createdAt, order.received_date),
    total,
    supplier: pickFirst(
      order.seller_name,
      order.seller,
      order.supplier,
      order.supplier_name,
      order.supplierName,
      order.provider,
    ),
    deliveryTerm: pickFirst(order.deliveryTerm, order.delivery_term, order.delivery, order.validity),
    priority: order.priority,
    currency: pickFirst(order.currency, order.total_currency, total?.currency),
    validity: order.validity,
    quote_number: order.quote_number,
    payment_conditions: pickFirst(order.payment_conditions, order.paymentConditions, order.payment_terms),
    delivery_term: pickFirst(order.delivery_term, order.deliveryTerm),
    notes: pickFirst(order.notes, order.observations, order.note),
  };

  if (Array.isArray(order.products)) {
    snapshot.products = order.products;
  } else if (Array.isArray(order.items)) {
    snapshot.items = order.items;
  }

  // Remove undefined/null fields to keep payload small
  return Object.fromEntries(
    Object.entries(snapshot).filter(
      ([, value]) => value !== undefined && value !== null && `${value}`.trim() !== '',
    ),
  );
};

/**
 * Solicita al agente un analisis de las ordenes existentes.
 * @param {Array<Object>} orders - Ordenes obtenidas del backend.
 * @param {string} [question] - Pregunta o foco adicional del usuario.
 * @param {string} [token] - Token de autenticacion (opcional).
 * @param {Object} [preferences] - Preferencias o aspectos adicionales.
 * @returns {Promise<{analysis: string, model: string, finish_reason?: string}>}
 */
export const analyzeOrders = async (orders, question, token, preferences) => {
  const normalizedOrders = Array.isArray(orders)
    ? orders
        .map(normalizeOrderForAgent)
        .filter((entry) => entry && Object.keys(entry).length > 0)
    : [];

  const payload = { orders: normalizedOrders };
  if (typeof question === 'string' && question.trim()) {
    payload.question = question.trim();
  }
  if (preferences && typeof preferences === 'object' && Object.keys(preferences).length > 0) {
    payload.preferences = preferences;
  }

  const headers = { 'Content-Type': 'application/json' };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  const response = await fetch(`${REACT_APP_BFF_URL}/agent/orders/analyze`, {
    method: 'POST',
    headers,
    body: JSON.stringify(payload),
  });

  const data = await response.json();
  if (!response.ok) {
    throw new Error(data?.detail || 'Error al analizar ordenes con el agente');
  }

  return data;
};
